import { LitElement, html, css } from 'lit';
import { getConfig } from '../config.js';

/**
 * Composant <ui-option>
 * Définit une option disponible pour le composant <ui-select>
 */
export class UIOption extends LitElement {
  static properties = {
    value: { type: String, reflect: true },
    disabled: { type: Boolean, reflect: true },
    selected: { type: Boolean, reflect: true }
  };

  static styles = css`
    :host {
      display: none; /* Masqué du DOM visible, géré par le parent <ui-select> */
    }
  `;

  constructor() {
    super();
    this.value = '';
    this.disabled = false;
    this.selected = false;
  }
}

if (!customElements.get('ui-option')) {
  customElements.define('ui-option', UIOption);
}

/**
 * Composant <ui-select>
 * Remplacement moderne et stylable de la balise <select> native, supporte single et multiple.
 */
export class UISelect extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: String, reflect: true }, // Format csv en mode multiple: "val1,val2"
    placeholder: { type: String },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    searchable: { type: Boolean },
    multiple: { type: Boolean },
    label: { type: String, reflect: true },
    
    // États internes
    isOpen: { type: Boolean, state: true },
    options: { type: Array, state: true },
    searchQuery: { type: String, state: true },
    focusedIndex: { type: Number, state: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
      user-select: none;
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .select-container {
      position: relative;
      width: 100%;
    }

    /* Le Header (Input principal) */
    .select-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: var(--ui-input-height);
      height: auto;
      padding: 4px var(--ui-padding-x);
      background-color: var(--ui-surface);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      cursor: pointer;
      box-shadow: var(--ui-shadow-sm);
      transition: var(--ui-transition-fast);
      outline: none;
      box-sizing: border-box;
    }

    .select-header:hover:not(.disabled) {
      border-color: var(--ui-border-hover);
      background-color: var(--ui-surface-hover);
    }

    .select-header:focus-visible:not(.disabled) {
      border-color: var(--ui-border-focus);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .select-header.open {
      border-color: var(--ui-border-focus);
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .select-header.disabled {
      cursor: not-allowed;
      opacity: 0.6;
      background-color: var(--ui-surface-active);
    }

    .select-value {
      flex: 1;
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      align-items: center;
      padding: 2px 0;
    }

    .select-placeholder {
      color: var(--ui-text-light);
    }

    /* Puces / Chips de multi-sélection */
    .select-chip {
      display: inline-flex;
      align-items: center;
      background-color: rgba(var(--ui-primary-rgb), 0.08);
      color: var(--ui-primary);
      font-size: var(--ui-font-size-sm);
      font-weight: 600;
      padding: 2px 8px;
      border-radius: var(--ui-radius-sm);
      user-select: none;
      box-sizing: border-box;
    }

    .select-chip-remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-left: 6px;
      cursor: pointer;
      font-weight: 700;
      font-size: 11px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background-color: rgba(var(--ui-primary-rgb), 0.1);
      transition: var(--ui-transition-fast);
    }

    .select-chip-remove:hover {
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
    }

    .select-arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      margin-left: 8px;
      color: var(--ui-text-muted);
      transition: transform var(--ui-transition-fast);
    }

    .select-header.open .select-arrow {
      transform: rotate(180deg);
      color: var(--ui-primary);
    }

    /* Le Dropdown */
    .select-dropdown {
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      width: 100%;
      background-color: color-mix(in srgb, var(--ui-surface) 90%, transparent);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-md);
      box-shadow: var(--ui-shadow-popover);
      z-index: var(--ui-z-dropdown);
      overflow: hidden;
      transform-origin: top;
      opacity: 0;
      transform: scale(0.95);
      pointer-events: none;
      transition: opacity var(--ui-transition-fast), transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .select-dropdown.open {
      opacity: 1;
      transform: scale(1);
      pointer-events: auto;
    }

    /* Input de recherche */
    .select-search-container {
      padding: 8px;
      border-bottom: 1px solid var(--ui-border);
    }

    .select-search-input {
      width: 100%;
      height: 36px;
      padding: 0 10px;
      border: 1px solid var(--ui-border);
      border-radius: var(--ui-radius-sm);
      font-family: inherit;
      font-size: inherit;
      color: inherit;
      background-color: var(--ui-surface-hover);
      outline: none;
      box-sizing: border-box;
      transition: var(--ui-transition-fast);
    }

    .select-search-input:focus {
      border-color: var(--ui-border-focus);
      background-color: var(--ui-surface);
    }

    /* Liste d'options */
    .select-options-list {
      max-height: 220px;
      overflow-y: auto;
      padding: 6px 0;
      margin: 0;
      list-style: none;
    }

    .select-option {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px var(--ui-padding-x);
      cursor: pointer;
      transition: var(--ui-transition-fast);
      font-size: var(--ui-font-size-base);
    }

    .select-option.hovered {
      background-color: var(--ui-surface-hover);
    }

    .select-option.selected {
      color: var(--ui-primary);
      font-weight: 600;
      background-color: rgba(var(--ui-primary-rgb), 0.06);
    }

    .select-option.disabled {
      cursor: not-allowed;
      opacity: 0.4;
      background-color: transparent;
    }

    .select-no-results {
      padding: 16px;
      text-align: center;
      color: var(--ui-text-light);
      font-size: var(--ui-font-size-sm);
    }

    .checkmark {
      width: 16px;
      height: 16px;
      fill: currentColor;
    }

    /* Backdrop pour version mobile */
    .backdrop {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(4px);
      z-index: calc(var(--ui-z-dropdown) - 1);
    }

    /* Style Responsive Mobile - Plein Écran / Bottom Sheet */
    @media (max-width: 576px) {
      .select-container.open .backdrop {
        display: block;
      }

      .select-dropdown {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        top: auto;
        width: 100%;
        max-width: 100%;
        border-radius: var(--ui-radius-lg) var(--ui-radius-lg) 0 0;
        transform: translateY(100%) scale(1);
        transform-origin: bottom;
        transition: transform var(--ui-transition-normal) cubic-bezier(0.25, 0.8, 0.25, 1), opacity var(--ui-transition-normal);
        z-index: var(--ui-z-dropdown);
      }

      .select-container.open .select-dropdown {
        transform: translateY(0) scale(1);
        opacity: 1;
      }

      .select-dropdown::before {
        content: '';
        display: block;
        width: 40px;
        height: 4px;
        background-color: var(--ui-border);
        border-radius: var(--ui-radius-full);
        margin: 8px auto 8px auto;
      }
      
      .select-options-list {
        max-height: 300px;
      }
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = '';
    this.placeholder = '';
    this.disabled = false;
    this.required = false;
    this.searchable = false;
    this.multiple = false;
    this.label = '';
    
    // États initiaux
    this.isOpen = false;
    this.options = [];
    this.searchQuery = '';
    this.focusedIndex = -1;

    this._handleOutsideClick = this._handleOutsideClick.bind(this);
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value);
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value && this.value.trim() !== '';
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez sélectionner une option.', this.shadowRoot.querySelector('.select-header'));
    } else {
      this.internals.setValidity({});
    }
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this._handleOutsideClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this._handleOutsideClick);
  }

  get selectedValues() {
    if (!this.value) return [];
    return this.multiple ? this.value.split(',').filter(Boolean) : [this.value];
  }

  /**
   * Observe les changements de slot pour reconstruire la liste d'options
   */
  _handleSlotChange(e) {
    const slot = e.target;
    const assignedNodes = slot.assignedNodes({ flatten: true });
    
    const uiOptions = assignedNodes.filter(
      node => node.nodeType === Node.ELEMENT_NODE && node.tagName.toLowerCase() === 'ui-option'
    );

    this.options = uiOptions.map(opt => ({
      value: opt.value || opt.getAttribute('value') || '',
      label: opt.textContent.trim(),
      disabled: opt.disabled || opt.hasAttribute('disabled'),
      selected: opt.selected || opt.hasAttribute('selected')
    }));

    // S'il n'y a pas encore de valeur, on applique les options sélectionnées par défaut
    if (!this.value) {
      const selectedOpts = this.options.filter(opt => opt.selected);
      if (selectedOpts.length > 0) {
        this.value = this.multiple 
          ? selectedOpts.map(opt => opt.value).join(',')
          : selectedOpts[0].value;
      }
    }
  }

  _handleOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target) && !e.composedPath().includes(this)) {
      this._closeDropdown();
    }
  }

  _toggleDropdown() {
    if (this.disabled) return;
    if (this.isOpen) {
      this._closeDropdown();
    } else {
      this._openDropdown();
    }
  }

  _openDropdown() {
    this.isOpen = true;
    this.focusedIndex = this.options.findIndex(opt => opt.value === this.value);
    
    // Focus l'input de recherche si disponible
    setTimeout(() => {
      const searchInput = this.shadowRoot.querySelector('.select-search-input');
      if (searchInput) searchInput.focus();
    }, 50);
  }

  _closeDropdown() {
    this.isOpen = false;
    this.searchQuery = '';
    this.focusedIndex = -1;
  }

  _selectOption(option) {
    if (option.disabled) return;
    
    const oldValue = this.value;
    
    if (this.multiple) {
      let current = this.selectedValues;
      if (current.includes(option.value)) {
        current = current.filter(val => val !== option.value);
      } else {
        current.push(option.value);
      }
      this.value = current.join(',');
    } else {
      this.value = option.value;
      this._closeDropdown();
      this.shadowRoot.querySelector('.select-header').focus();
    }

    if (oldValue !== this.value) {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      this.dispatchEvent(new CustomEvent('ui-change', {
        detail: { 
          value: this.value,
          selected: this.selectedValues,
          label: this.multiple 
            ? this.options.filter(opt => this.selectedValues.includes(opt.value)).map(opt => opt.label).join(', ')
            : option.label
        },
        bubbles: true,
        composed: true
      }));
    }
  }

  _removeChip(valueToRemove, e) {
    e.stopPropagation(); // Évite d'ouvrir le menu
    const oldValue = this.value;
    const current = this.selectedValues.filter(val => val !== valueToRemove);
    this.value = current.join(',');

    if (oldValue !== this.value) {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      this.dispatchEvent(new CustomEvent('ui-change', {
        detail: { 
          value: this.value, 
          selected: this.selectedValues,
          label: this.options.filter(opt => this.selectedValues.includes(opt.value)).map(opt => opt.label).join(', ')
        },
        bubbles: true,
        composed: true
      }));
    }
  }

  _handleSearchInput(e) {
    this.searchQuery = e.target.value;
    this.focusedIndex = 0;
  }

  _handleKeyDown(e) {
    if (this.disabled) return;

    const filteredOptions = this._getFilteredOptions();

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!this.isOpen) {
          this._openDropdown();
        } else if (this.focusedIndex >= 0 && this.focusedIndex < filteredOptions.length) {
          this._selectOption(filteredOptions[this.focusedIndex]);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!this.isOpen) {
          this._openDropdown();
        } else if (filteredOptions.length > 0) {
          this.focusedIndex = (this.focusedIndex + 1) % filteredOptions.length;
          this._scrollToFocused();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!this.isOpen) {
          this._openDropdown();
        } else if (filteredOptions.length > 0) {
          this.focusedIndex = (this.focusedIndex - 1 + filteredOptions.length) % filteredOptions.length;
          this._scrollToFocused();
        }
        break;
      case 'Escape':
        if (this.isOpen) {
          e.preventDefault();
          this._closeDropdown();
          this.shadowRoot.querySelector('.select-header').focus();
        }
        break;
      case 'Tab':
        if (this.isOpen) {
          this._closeDropdown();
        }
        break;
    }
  }

  _getFilteredOptions() {
    if (!this.searchQuery) return this.options;
    const query = this.searchQuery.toLowerCase();
    return this.options.filter(opt => opt.label.toLowerCase().includes(query));
  }

  _scrollToFocused() {
    setTimeout(() => {
      const list = this.shadowRoot.querySelector('.select-options-list');
      const focusedItem = list?.querySelector('.select-option.hovered');
      if (list && focusedItem) {
        const listHeight = list.clientHeight;
        const itemTop = focusedItem.offsetTop;
        const itemHeight = focusedItem.clientHeight;

        if (itemTop + itemHeight > list.scrollTop + listHeight) {
          list.scrollTop = itemTop + itemHeight - listHeight;
        } else if (itemTop < list.scrollTop) {
          list.scrollTop = itemTop;
        }
      }
    }, 10);
  }

  render() {
    const filteredOptions = this._getFilteredOptions();
    const selectedValues = this.selectedValues;
    const selectedOptions = this.options.filter(opt => selectedValues.includes(opt.value));
    
    const displayPlaceholder = this.placeholder || getConfig('select', 'placeholder');
    const noResultsText = getConfig('select', 'noResultsText');
    const searchText = getConfig('select', 'searchText');

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="select-container ui-component ${this.isOpen ? 'open' : ''}">
        <!-- Slot invisible pour charger les ui-option du DOM parent -->
        <slot style="display: none;" @slotchange="${this._handleSlotChange}"></slot>
        
        <!-- Soumission automatique gérée via ElementInternals -->

        <!-- Backdrop mobile -->
        <div class="backdrop" @click="${this._closeDropdown}"></div>

        <!-- Header clickable -->
        <div 
          class="select-header ${this.isOpen ? 'open' : ''} ${this.disabled ? 'disabled' : ''}" part="input"
          tabindex="${this.disabled ? '-1' : '0'}"
          @click="${this._toggleDropdown}"
          @keydown="${this._handleKeyDown}"
        >
          <div class="select-value">
            ${selectedOptions.length > 0 
              ? (this.multiple 
                  ? selectedOptions.map(opt => html`
                      <span class="select-chip">
                        ${opt.label}
                        <span class="select-chip-remove" @click="${(e) => this._removeChip(opt.value, e)}">&times;</span>
                      </span>
                    `)
                  : selectedOptions[0].label
                )
              : html`<span class="select-placeholder">${displayPlaceholder}</span>`
            }
          </div>
          <span class="select-arrow">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 14px; height: 14px;">
              <polyline points="6 9 10 13 14 9"></polyline>
            </svg>
          </span>
        </div>

        <!-- Dropdown contenant la recherche et la liste -->
        <div class="select-dropdown ${this.isOpen ? 'open' : ''}">
          ${this.searchable ? html`
            <div class="select-search-container">
              <input 
                type="text" 
                class="select-search-input" 
                placeholder="${searchText}"
                .value="${this.searchQuery}"
                @input="${this._handleSearchInput}"
                @keydown="${this._handleKeyDown}"
              >
            </div>
          ` : ''}
          
          <ul class="select-options-list" role="listbox">
            ${filteredOptions.length === 0 ? html`
              <li class="select-no-results">${noResultsText}</li>
            ` : filteredOptions.map((opt, index) => {
              const isSelected = selectedValues.includes(opt.value);
              const isHovered = index === this.focusedIndex;
              return html`
                <li 
                  class="select-option ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${opt.disabled ? 'disabled' : ''}"
                  role="option"
                  aria-selected="${isSelected}"
                  @click="${() => this._selectOption(opt)}"
                  @mouseenter="${() => { if (!opt.disabled) this.focusedIndex = index; }}"
                >
                  <span>${opt.label}</span>
                  ${isSelected ? html`
                    <svg class="checkmark" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                  ` : ''}
                </li>
              `;
            })}
          </ul>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('ui-select')) {
  customElements.define('ui-select', UISelect);
}
